import { selectAccountId, selectUserRolePermissions } from '@socialive/common';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectApiBaseUrl, selectAuthToken } from '~/store';
import '../../style/modal.css';
import AccountSelector from '../AccountSelector';

const customStyles = {
  content: {
    top: '65px',
    left: 'auto',
    right: '10px',
    bottom: 'auto',
    minWidth: '354px',
    overflow: 'hidden',
    padding: '10px',
  },
  overlay: {
    backgroundColor: 'rgb(0,0,0,0.0)',
    zIndex: '3',
  },
};

export default function DropdownMenu({ handleModal, modal, switchAccount, trackEvent }) {
  const history = useHistory();
  const { canManageChannels, canManageUsers, canManagePlayers, canManageIntegrations, canManageSubscriptions } =
    useSelector(selectUserRolePermissions);
  const authorization = useSelector(selectAuthToken);
  const apiBaseUrl = useSelector(selectApiBaseUrl);
  const accountId = useSelector(selectAccountId);

  const isChargebeeSession = useMemo(() => sessionStorage.getItem('isChargebeeSession') === 'true', []);

  const defaultParams = useMemo(
    () => ({
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        authorization,
      },
      method: 'GET',
    }),
    [authorization]
  );

  function handleClick(route) {
    history.push(route);
    handleModal(false, false);
  }

  function handleChargebee() {
    const params = {
      ...defaultParams,
      method: 'POST',
    };

    handleModal(false, false);
    window.chargebeeInstance = window.Chargebee.init({ site: 'socialive' });
    window.chargebeeInstance = window.Chargebee.getInstance();

    fetch(`${apiBaseUrl}/cb/portal?s=${sessionStorage.getItem('subscriberId')}`, params)
      .then((response) => response.json())
      .then((data) => {
        const payload = new Promise((resolve, reject) => {
          if (data.portal_session) {
            resolve(data.portal_session);
          } else {
            reject();
          }
        });
        window.chargebeeInstance.setPortalSession(() => payload);
        const cbPortal = window.chargebeeInstance.createChargebeePortal();

        cbPortal.open({
          close() {},
        });
      });
  }

  const handleLearningHub = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/lms`, defaultParams);
      let url = await response.text();
      url = new URL(url);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error('Something went wrong');
    }
  }, [apiBaseUrl, defaultParams]);

  return (
    <Modal
      isOpen={modal.main}
      onRequestClose={() => handleModal(false, false)}
      style={customStyles}
      contentLabel="Create Broadcast Modal"
    >
      <AccountSelector
        accountId={accountId}
        APIBaseURL={apiBaseUrl}
        closeModal={() => handleModal(false, false)}
        switchAccount={switchAccount}
        trackEvent={trackEvent}
      />
      <ul className="account-nav-items">
        <li className="users account-nav-item">
          <button type="button" onClick={() => handleClick('/users')} disabled={!canManageUsers}>
            Users
          </button>
        </li>
        <li className="channels account-nav-item">
          <button type="button" onClick={() => handleClick('/channels')} disabled={!canManageChannels}>
            Channels
          </button>
        </li>
        <li className="player account-nav-item">
          <button type="button" onClick={() => handleClick('/players')} disabled={!canManagePlayers}>
            Players
          </button>
        </li>
        <li className="connections account-nav-item last">
          <button type="button" onClick={() => handleClick('/integrations')} disabled={!canManageIntegrations}>
            Integrations
          </button>
        </li>
        {isChargebeeSession && canManageSubscriptions && (
          <li className="subscription account-nav-item">
            <button type="button" onClick={handleChargebee}>
              Manage subscription
            </button>
          </li>
        )}
        <li className="help account-nav-item">
          <a
            href="https://go.socialive.us/support-center"
            target="help"
            rel="noopener noreferrer"
            onClick={() => handleModal(false, false)}
          >
            Help center
          </a>
        </li>
        <li className="hub account-nav-item">
          <button type="button" onClick={handleLearningHub}>
            Socialive Learning Hub
          </button>
        </li>
        <li className={`apps account-nav-item ${modal.appMenu ? 'open' : ''}`}>
          <button type="button" onClick={() => handleModal(true, !modal.appMenu)}>
            Mobile apps
          </button>
          <ul className="applinks-list">
            <li className="applinks-list-item iOS" key="iosAppLink">
              <a href="https://apps.apple.com/us/app/socialive/id1150674833" target="_blank" rel="noopener noreferrer">
                Socialive for iOS
              </a>
            </li>
            <li className="applinks-list-item Android" key="androidAppLink">
              <a
                href="https://play.google.com/store/apps/details?id=us.socialive.socialiveapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                Socialive for Android
              </a>
            </li>
          </ul>
        </li>
        <li className="site account-nav-item last">
          <a
            href="https://www.socialive.us/"
            target="socialive"
            rel="noopener noreferrer"
            onClick={() => handleModal(false, false)}
          >
            Socialive.us website
          </a>
        </li>
        <li className="reset-password account-nav-item">
          <Link to="/resetpassword">Reset password</Link>
        </li>
        <li className="logout account-nav-item">
          <Link to="/logout">Logout</Link>
        </li>
      </ul>
    </Modal>
  );
}

DropdownMenu.propTypes = {
  handleModal: PropTypes.func.isRequired,
  modal: PropTypes.shape({
    appMenu: PropTypes.bool.isRequired,
    main: PropTypes.bool.isRequired,
  }).isRequired,
  switchAccount: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};
